// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Type Definitions
// ═══════════════════════════════════════════════════════════════

import { primary, secondary, semantic } from '../lib/colors';
export interface User {
  id: string;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: string;           // App-level role name (e.g., "User", "Admin")
  topLevelRole: string;   // Platform role: "super_admin" | "admin" | "participant"
  isAdmin: boolean;
  active: boolean;
  applicationRolesIds: string[];
  lastLogin: number;
}

export interface TableRight {
  name: string;
  applicationTableKey: string;
  view: boolean;
  update: boolean;
  create: boolean;
  delete: boolean;
}

export interface AuthSession {
  user: User;
  token: string;
  rights: TableRight[];
  expiresAt: number;
}

export interface Incident {
  id: number;           // Rid
  name: string;
  incidentType: IncidentType;
  status: IncidentStatus;
  location: string;
  latitude: string;
  longitude: string;
  description: string;
  commander: string;
  startDate: string;
  createdByName: string;
  datecreated?: string;
}

export type IncidentStatus = 'active' | 'monitoring' | 'closed' | 'archived';
export type IncidentType =
  | 'natural_disaster' | 'mci' | 'hazmat' | 'exercise' | 'training' | 'special_event' | 'other'
  | 'hurricane' | 'wildfire' | 'flood' | 'earthquake' | 'tornado'
  | 'planned' | 'general';

export interface ActivityLogEntry {
  id: number;
  incidentId: string;
  module: string;
  action: string;
  actorName: string;
  actorEmail: string;
  targetType: string;
  targetId: string;
  details: string;
  datecreated?: string;
}

export const INCIDENT_TYPE_META: Record<string, { label: string; color: string; icon: string }> = {
  natural_disaster: { label: 'Natural Disaster', color: '#0EA5E9', icon: 'IconCloudStorm' },
  mci:             { label: 'Mass Casualty',     color: semantic.error, icon: 'IconAmbulance' },
  hazmat:          { label: 'Hazmat',            color: '#A67103', icon: 'IconBiohazard' },
  exercise:        { label: 'Exercise',          color: '#A627FF', icon: 'IconClipboardCheck' },
  training:        { label: 'Training',          color: '#07963F', icon: 'IconSchool' },
  special_event:   { label: 'Special Event',    color: '#E85D04', icon: 'IconConfetti' },
  other:           { label: 'Other',             color: '#7B7D7F', icon: 'IconInfoCircle' },
  // Legacy types from older incidents
  hurricane:       { label: 'Hurricane',         color: '#0EA5E9', icon: 'IconCloudStorm' },
  wildfire:        { label: 'Wildfire',          color: '#D97706', icon: 'IconFlame' },
  flood:           { label: 'Flood',             color: '#0EA5E9', icon: 'IconDroplet' },
  earthquake:      { label: 'Earthquake',        color: semantic.error, icon: 'IconBuildingBridge' },
  tornado:         { label: 'Tornado',           color: '#6366F1', icon: 'IconTornado' },
  planned:         { label: 'Planned',           color: primary[700], icon: 'IconCalendar' },
  general:         { label: 'General',           color: '#64748B', icon: 'IconShield' },
};

export interface HubModule {
  id: string;
  name: string;
  description: string;
  icon: string;
  route: string;
  color: string;
  tables: string[];    // Table keys this module uses (for permission checks)
}

export type Theme = 'light' | 'dark';

// ── HotWash ──────────────────────────────────────────────────

export type ObservationTier = 'observation' | 'improvement_needed' | 'problem_identified' | 'critical_error';
export type ObservationCategory = 'communication' | 'logistics' | 'personnel' | 'equipment' | 'procedures' | 'medical' | 'other';
export type ObservationPhase = 'preparation' | 'response' | 'recovery' | 'mitigation';

export interface Observation {
  id: number;           // Rid
  incidentId: string;
  tier: ObservationTier;
  title: string;
  description: string;
  category: ObservationCategory;
  phase: ObservationPhase;
  suggestedRemedy: string;
  isAnonymous: boolean;
  submittedBy: string;
  datecreated?: string;
}

export const TIER_META: Record<ObservationTier, { label: string; color: string; bg: string; desc: string }> = {
  observation:        { label: 'Observation',        color: semantic.info, bg: '#F1FAFC', desc: 'Neutral finding' },
  improvement_needed: { label: 'Improvement Needed', color: semantic.warning, bg: '#FDF8F1', desc: 'Area for growth' },
  problem_identified: { label: 'Problem Identified', color: semantic.error, bg: '#FCF7F7', desc: 'Significant issue' },
  critical_error:     { label: 'Critical Error',     color: '#610F11', bg: '#FCF7F7', desc: 'Requires immediate action' },
};

export const TIER_KEYS: ObservationTier[] = ['observation', 'improvement_needed', 'problem_identified', 'critical_error'];
export const CATEGORIES: ObservationCategory[] = ['communication', 'logistics', 'personnel', 'equipment', 'procedures', 'medical', 'other'];
export const PHASES: ObservationPhase[] = ['preparation', 'response', 'recovery', 'mitigation'];

// ── Handoff ──────────────────────────────────────────────────

export type HandoffNoteStatus = 'draft' | 'submitted' | 'acknowledged';
export type ShiftType = 'day' | 'night';
export type ActionPriority = 'low' | 'normal' | 'high' | 'critical';
export type ActionStatus = 'pending' | 'completed';

export interface HandoffEvent {
  id: number;
  name: string;
  eventType: string;
  status: string;
  description: string;
  aiBriefing: string;
  aiPriorities: string;
  creatorName: string;
  creatorEmail: string;
  datecreated?: string;
}

export interface Position {
  id: number;
  eventId: string;
  positionName: string;
  shortName: string;
  sortOrder: string;
  status: string;
  dayUserName: string;
  dayUserEmail: string;
  nightUserName: string;
  nightUserEmail: string;
  datecreated?: string;
}

export interface HandoffSection {
  title: string;
  content: string;
}

export interface HandoffNote {
  id: number;
  eventId: string;
  positionId: string;
  positionName: string;
  status: HandoffNoteStatus;
  fromShift: ShiftType;
  toShift: ShiftType;
  shiftDate: string;
  sections: HandoffSection[];
  senderName: string;
  senderSignature: string;
  recipientName: string;
  recipientSignature: string;
  aiSummary: string;
  submittedAt: string;
  acknowledgedAt: string;
  datecreated?: string;
}

export interface ActionItem {
  id: number;
  eventId: string;
  handoffNoteId: string;
  title: string;
  description: string;
  status: ActionStatus;
  priority: ActionPriority;
  assignedToName: string;
  deadline: string;
  creatorName: string;
  completedByName: string;
  completedAt: string;
  datecreated?: string;
}

export interface HandoffTemplate {
  id: number;
  name: string;
  description: string;
  sections: Array<{ title: string; placeholder: string }>;
  isDefault: string;
  datecreated?: string;
}

export const DEFAULT_ICS_POSITIONS = [
  { name: 'Incident Commander', short: 'IC', order: '1' },
  { name: 'Operations Section Chief', short: 'OPS', order: '2' },
  { name: 'Planning Section Chief', short: 'PLAN', order: '3' },
  { name: 'Logistics Section Chief', short: 'LOG', order: '4' },
  { name: 'Finance/Admin Section Chief', short: 'FIN', order: '5' },
  { name: 'Public Information Officer', short: 'PIO', order: '6' },
  { name: 'Safety Officer', short: 'SO', order: '7' },
  { name: 'Liaison Officer', short: 'LNO', order: '8' },
  { name: 'Communications', short: 'COMMS', order: '9' },
  { name: 'Situation Unit Leader', short: 'SITL', order: '10' },
];

export const DEFAULT_TEMPLATE_SECTIONS: Array<{ title: string; placeholder: string }> = [
  { title: 'Situation Overview', placeholder: 'Current situation and status...' },
  { title: 'Significant Activities', placeholder: 'Key events during this shift...' },
  { title: 'Ongoing Actions', placeholder: 'Actions in progress...' },
  { title: 'Issues & Concerns', placeholder: 'Problems or concerns to be aware of...' },
  { title: 'Recommendations for Next Shift', placeholder: 'Recommendations for incoming personnel...' },
];

export const PRIORITY_META: Record<ActionPriority, { label: string; color: string }> = {
  low: { label: 'Low', color: secondary[700] },
  normal: { label: 'Normal', color: semantic.info },
  high: { label: 'High', color: semantic.warning },
  critical: { label: 'Critical', color: semantic.error },
};

// ── RollCall ──────────────────────────────────────────────────

export type RCEventType = 'shift' | 'meal' | 'training' | 'briefing' | 'custom';
export type RCEventStatus = 'open' | 'closed';
export type RCMealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';
export type PersonnelStatus = 'active' | 'inactive';
export type CheckInMethod = 'pin' | 'badge' | 'mobile' | 'manual';

export interface Personnel {
  id: number;
  firstName: string;
  lastName: string;
  agency: string;
  position: string;
  pin: string;
  badgeId: string;
  phone: string;
  email: string;
  notes: string;
  status: PersonnelStatus;
  creator: string;
  datecreated?: string;
}

export interface RCEvent {
  id: number;
  incidentId: string;
  eventType: RCEventType;
  mealType: string;
  name: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: string;
  notes: string;
  eventStatus: RCEventStatus;
  autoCheckoutHours: string;
  creator: string;
  datecreated?: string;
}

export interface RCCheckin {
  id: number;
  eventId: string;
  personnelId: string;
  personnelName: string;
  agency: string;
  position: string;
  checkInTime: string;
  checkOutTime: string;
  method: CheckInMethod;
  geoLat: string;
  geoLng: string;
  geoAccuracy: string;
  checkedOutBy: string;
  datecreated?: string;
}

export const RC_EVENT_TYPE_META: Record<RCEventType, { label: string; color: string }> = {
  shift: { label: 'Shift', color: 'blue' },
  meal: { label: 'Meal', color: 'green' },
  training: { label: 'Training', color: 'yellow' },
  briefing: { label: 'Briefing', color: 'indigo' },
  custom: { label: 'Custom', color: 'gray' },
};

export const ICS_POSITIONS = [
  { value: 'incident_commander', label: 'Incident Commander' },
  { value: 'operations_chief', label: 'Operations Section Chief' },
  { value: 'planning_chief', label: 'Planning Section Chief' },
  { value: 'logistics_chief', label: 'Logistics Section Chief' },
  { value: 'finance_chief', label: 'Finance/Admin Section Chief' },
  { value: 'safety_officer', label: 'Safety Officer' },
  { value: 'pio', label: 'Public Information Officer' },
  { value: 'liaison', label: 'Liaison Officer' },
  { value: 'operations', label: 'Operations' },
  { value: 'planning', label: 'Planning' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'communications', label: 'Communications' },
  { value: 'medical', label: 'Medical' },
  { value: 'responder', label: 'Responder' },
  { value: 'volunteer', label: 'Volunteer' },
  { value: 'support', label: 'Support Staff' },
  { value: 'other', label: 'Other' },
];
